/**
 * Budgley Breakdown Module
 * Generates budget summary reports showing spending progress, warnings, and insights
 */

import { auth, db } from './firebase.js';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { getCurrentBudgetData, getCurrentVersionName } from './budget.js';

/**
 * Generate a comprehensive budget breakdown summary
 * @param {string} budgetId - The budget version to analyze
 * @param {number} periodDays - Number of days to include in analysis (default: 7 for weekly)
 * @returns {Object} Summary object with all breakdown data
 */
export async function generateBreakdownSummary(budgetId = null, periodDays = 7) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7); // "YYYY-MM"

  // Get budget data using the budget module's function
  const budget = await getCurrentBudgetData();
  const budgetName = getCurrentVersionName();

  if (!budget) {
    throw new Error('No budget found');
  }

  // Calculate period date range
  const periodStartDate = new Date();
  periodStartDate.setDate(periodStartDate.getDate() - periodDays);

  // Month-to-date calculations
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const currentDay = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - currentDay;

  // First, build a category ID to name mapping from the budget
  const categories = budget.categories || {};
  const categoryIdToName = {};

  Object.keys(categories).forEach(catIndex => {
    const category = categories[catIndex];
    categoryIdToName[catIndex] = category.name || `Category ${catIndex}`;
  });

  // Get all expenses for current month
  const expensesRef = collection(db, 'users', user.uid, 'expenses');
  const monthQuery = query(expensesRef, where('month', '==', currentMonth));
  const expenseSnapshot = await getDocs(monthQuery);

  const allExpenses = [];
  const categorySpending = {};
  let totalSpent = 0;
  let unplannedTotal = 0;

  expenseSnapshot.forEach(doc => {
    const expense = { id: doc.id, ...doc.data() };
    allExpenses.push(expense);
    totalSpent += expense.amount || 0;

    if (expense.isUnplanned) {
      unplannedTotal += expense.amount || 0;
    }

    // Track category spending - look up category name from budget
    // Try multiple sources: categoryId from expense, or stored categoryName
    const categoryId = expense.categoryId;
    let categoryName = 'Uncategorized';

    if (categoryId !== undefined && categoryId !== null && categoryId !== '') {
      // Look up in budget categories
      categoryName = categoryIdToName[categoryId] || categoryIdToName[String(categoryId)] || expense.categoryName || 'Uncategorized';
    } else if (expense.categoryName) {
      // Fall back to stored category name
      categoryName = expense.categoryName;
    }

    // Use categoryId as key, or category name if no ID
    const categoryKey = categoryId !== undefined && categoryId !== null && categoryId !== '' ? categoryId : categoryName;

    if (!categorySpending[categoryKey]) {
      categorySpending[categoryKey] = {
        spent: 0,
        budgeted: 0,
        categoryName: categoryName,
        expenses: []
      };
    }
    categorySpending[categoryKey].spent += expense.amount || 0;
    categorySpending[categoryKey].expenses.push(expense);

    // Update expense object with category name for later use
    expense.categoryName = categoryName;
  });

  // Calculate total budgeted amount
  let totalBudgeted = 0;

  Object.keys(categories).forEach(catIndex => {
    const category = categories[catIndex];
    const items = category.items || [];

    items.forEach(item => {
      const monthlyAmount = convertToMonthly(item.amount || 0, item.frequency || 'monthly');
      totalBudgeted += monthlyAmount;

      // Add budgeted amount to category tracking
      if (categorySpending[catIndex]) {
        categorySpending[catIndex].budgeted += monthlyAmount;
      } else {
        categorySpending[catIndex] = {
          spent: 0,
          budgeted: monthlyAmount,
          categoryName: categoryIdToName[catIndex] || `Category ${catIndex}`,
          expenses: []
        };
      }
    });
  });

  // Get recurring expenses for upcoming week
  const upcomingRecurring = await getUpcomingRecurringExpenses(user.uid, budget);

  // Calculate spending metrics
  const remaining = totalBudgeted - totalSpent;
  const percentSpent = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;
  const avgDailySpend = currentDay > 0 ? totalSpent / currentDay : 0;
  const projectedTotal = totalSpent + (avgDailySpend * daysRemaining);
  const recommendedDailySpend = daysRemaining > 0 ? remaining / daysRemaining : 0;

  // Determine budget health
  const budgetHealth = getBudgetHealth(percentSpent);

  // Get top 3 categories by spending
  const topCategories = Object.values(categorySpending)
    .filter(cat => cat.spent > 0)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 3);

  // Identify warning categories (80-99% spent) and over-budget categories (≥100%)
  const warningCategories = [];
  const overBudgetCategories = [];

  Object.values(categorySpending).forEach(cat => {
    if (cat.budgeted > 0) {
      const percentUsed = (cat.spent / cat.budgeted) * 100;
      if (percentUsed >= 100) {
        overBudgetCategories.push({
          name: cat.categoryName,
          spent: cat.spent,
          budgeted: cat.budgeted,
          percentUsed,
          overage: cat.spent - cat.budgeted
        });
      } else if (percentUsed >= 80) {
        warningCategories.push({
          name: cat.categoryName,
          spent: cat.spent,
          budgeted: cat.budgeted,
          percentUsed,
          remaining: cat.budgeted - cat.spent
        });
      }
    }
  });

  // Helper function to format expense for display
  const formatExpenseForDisplay = (exp) => {
    // Format description: Line Item - Description, or just Line Item
    let displayDesc = exp.lineItemName || 'Expense';
    if (exp.description && exp.description !== exp.lineItemName) {
      displayDesc = `${exp.lineItemName} - ${exp.description}`;
    } else if (!exp.lineItemName && exp.description) {
      displayDesc = exp.description;
    }

    // Convert Firestore Timestamp to JavaScript Date
    let expenseDate = exp.date;
    if (expenseDate && typeof expenseDate.toDate === 'function') {
      expenseDate = expenseDate.toDate();
    } else if (expenseDate && typeof expenseDate === 'string') {
      expenseDate = new Date(expenseDate);
    }

    return {
      description: displayDesc,
      amount: exp.amount || 0,
      date: expenseDate,
      categoryName: exp.categoryName || 'Uncategorized',
      isUnplanned: exp.isUnplanned || false
    };
  };

  // Get 5 largest expenses
  const largestExpenses = allExpenses
    .sort((a, b) => (b.amount || 0) - (a.amount || 0))
    .slice(0, 5)
    .map(formatExpenseForDisplay);

  // Get unplanned expenses
  const unplannedExpenses = allExpenses
    .filter(exp => exp.isUnplanned)
    .sort((a, b) => (b.amount || 0) - (a.amount || 0))
    .map(formatExpenseForDisplay);

  // Build summary object
  const summary = {
    generatedAt: now,
    periodDays,
    periodStart: periodStartDate,
    periodEnd: now,
    budgetName: budgetName,

    // Overall metrics
    overall: {
      totalSpent,
      totalBudgeted,
      remaining,
      percentSpent,
      daysRemaining,
      currentDay,
      daysInMonth,
      budgetHealth
    },

    // Spending pace
    pace: {
      avgDailySpend,
      projectedTotal,
      recommendedDailySpend,
      onTrack: projectedTotal <= totalBudgeted
    },

    // Category insights
    categories: {
      top: topCategories,
      warnings: warningCategories,
      overBudget: overBudgetCategories
    },

    // Expense details
    expenses: {
      largest: largestExpenses,
      unplanned: unplannedExpenses,
      unplannedTotal,
      unplannedCount: unplannedExpenses.length
    },

    // Upcoming recurring
    recurring: {
      upcoming: upcomingRecurring,
      count: upcomingRecurring.length,
      total: upcomingRecurring.reduce((sum, exp) => sum + (exp.amount || 0), 0)
    }
  };

  return summary;
}

/**
 * Convert any frequency amount to monthly equivalent
 */
function convertToMonthly(amount, frequency) {
  const conversions = {
    'weekly': amount * 52 / 12,
    'biweekly': amount * 26 / 12,
    'monthly': amount,
    'quarterly': amount / 3,
    'yearly': amount / 12,
    'annual': amount / 12
  };
  return conversions[frequency] || amount;
}

/**
 * Get budget health status based on percentage spent
 */
function getBudgetHealth(percentSpent) {
  if (percentSpent <= 80) {
    return {
      status: 'On Track',
      class: 'success',
      emoji: '✅',
      message: 'You\'re doing great! Keep it up.'
    };
  } else if (percentSpent <= 100) {
    return {
      status: 'Warning',
      class: 'warning',
      emoji: '⚠️',
      message: 'Watch your spending to stay on budget.'
    };
  } else {
    return {
      status: 'Over Budget',
      class: 'danger',
      emoji: '🚨',
      message: 'You\'ve exceeded your budget this month.'
    };
  }
}

/**
 * Get upcoming recurring expenses for the next 7 days
 */
async function getUpcomingRecurringExpenses(userId, budget) {
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7);
  const oneWeekFromNow = new Date();
  oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

  // Get all recurring expenses for current month
  const expensesRef = collection(db, 'users', userId, 'expenses');
  const monthQuery = query(expensesRef, where('month', '==', currentMonth));
  const expenseSnapshot = await getDocs(monthQuery);

  const completedRecurring = new Set();
  expenseSnapshot.forEach(doc => {
    const expense = doc.data();
    if (expense.isRecurringPayment && expense.recurringId) {
      completedRecurring.add(expense.recurringId);
    }
  });

  // Check all budget items for upcoming recurring expenses
  const upcomingExpenses = [];
  const categories = budget.categories || {};

  Object.keys(categories).forEach(catIndex => {
    const category = categories[catIndex];
    const items = category.items || [];

    items.forEach((item, itemIndex) => {
      if (item.isRecurring && item.recurringConfig) {
        const recurringId = `${catIndex}-${itemIndex}`;

        // Skip if already completed this month
        if (completedRecurring.has(recurringId)) {
          return;
        }

        const dueDate = calculateNextDueDate(item.recurringConfig, now);

        if (dueDate && dueDate <= oneWeekFromNow) {
          upcomingExpenses.push({
            name: item.label || 'Recurring Expense',
            amount: item.amount || 0,
            dueDate,
            categoryName: category.name || 'Uncategorized',
            recurringId
          });
        }
      }
    });
  });

  return upcomingExpenses.sort((a, b) => a.dueDate - b.dueDate);
}

/**
 * Calculate next due date for a recurring expense
 */
function calculateNextDueDate(recurringConfig, fromDate) {
  if (!recurringConfig) return null;

  const now = fromDate || new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const currentDay = now.getDate();

  if (recurringConfig.type === 'fixed') {
    const dayOfMonth = recurringConfig.dayOfMonth || 1;
    const dueDate = new Date(currentYear, currentMonth, dayOfMonth);

    // If due date already passed this month, return null
    if (dueDate < now) {
      return null;
    }

    return dueDate;
  } else if (recurringConfig.type === 'relative') {
    const weekNumber = recurringConfig.weekNumber || 1;
    const dayOfWeek = recurringConfig.dayOfWeek || 1;

    const firstDay = new Date(currentYear, currentMonth, 1);
    const firstWeekday = firstDay.getDay();
    const offset = (dayOfWeek - firstWeekday + 7) % 7;
    const targetDay = 1 + offset + ((weekNumber - 1) * 7);

    const dueDate = new Date(currentYear, currentMonth, targetDay);

    if (dueDate < now) {
      return null;
    }

    return dueDate;
  }

  return null;
}

/**
 * Save breakdown to history (optional for future use)
 */
export async function saveBreakdownHistory(summary) {
  const user = auth.currentUser;
  if (!user) return;

  try {
    const historyRef = collection(db, 'users', user.uid, 'breakdownHistory');
    await addDoc(historyRef, {
      ...summary,
      deliveryMethod: 'app',
      deliveryStatus: 'viewed',
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving breakdown history:', error);
  }
}

/**
 * Format currency for display
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}
